import { alpha, createTheme } from '@mui/material/styles';
import type { CSSInterpolation } from '@mui/material/styles';

import { palette } from './palette';

/**
 * Custom slab themes that are used throughout the app, and located
 * in one place. Used to overwrite MUI defaults
 */
export const SlabTheme = createTheme({
  palette,
  typography: {
    allVariants: {
      color: palette.SlabGray[800],
    },
    fontFamily: [
      'DM Sans',
      '-apple-system',
      'BlinkMacSystemFont',
      'Helvetica Neue',
      'Roboto',
      'sans-serif',
    ].join(','),
    body1: {
      fontWeight: 400,
      fontSize: '1rem', // 16px
    },
    body2: {
      fontSize: '1rem', // 16px
    },
    body3: {
      fontSize: '0.875rem', // 14px
      fontWeight: 400,
      lineHeight: '1.125rem', // 18px
      color: palette.SlabGray[600],
    },
    h1: {
      fontSize: '3rem', // 48px
      fontWeight: 500,
      letterSpacing: 0,
    },
    h2: {
      fontSize: '1.75rem', // 28px
      fontWeight: 400,
      letterSpacing: 0,
    },
    h3: {
      fontWeight: 500,
      fontSize: '2.25rem', // 36px
    },
    h4: {
      fontWeight: 400,
      fontSize: '1.75rem', // 28px
    },
    h5: {
      fontWeight: 400,
      fontSize: '1.25rem', // 20px
    },
    'h5/medium': {
      fontWeight: 500,
      fontSize: '1.25rem', // 20px
    },
    h6: {
      fontSize: '1.125rem', // 18px
      fontWeight: 400,
    },
    'h6/medium': {
      fontWeight: 500,
      fontSize: '1.125rem', // 18px
    },
    'h6/regular/link': {
      fontWeight: 400,
      fontSize: '1.125rem', // 18px
      textDecoration: 'underline',
    },
    'h6/regular/button': {
      fontWeight: 400,
      fontSize: '1.125rem', // 18px
      lineHeight: '1.125rem', // 18px
      textTransform: 'uppercase',
    },
    capture: {
      fontWeight: 400,
      fontSize: '0.75rem', // 12px
    },
    secondary: {
      fontWeight: 400,
      color: palette.SlabGray[600],
    },
  },
  zIndex: {
    tableHead: 2,
    tableRow: 1,
    tableStickyCell: 3,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        a: {
          color: palette.SlabIndigo[700],
        },
      },
    },
    MuiLink: {
      defaultProps: {
        color: 'SlabIndigo.700',
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: ({ theme }): CSSInterpolation => ({
          fill: theme.palette.SlabIndigo['500'],
        }),
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: ({ theme }): CSSInterpolation => ({
          '&.Mui-checked:not(.Mui-disabled)': {
            color: theme.palette.SlabIndigo[700],
          },
        }),
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: ({ theme }): CSSInterpolation => ({
          '&.Mui-checked:not(.Mui-disabled), &.MuiCheckbox-indeterminate:not(.Mui-disabled)': {
            color: theme.palette.SlabIndigo[700],
          },
        }),
      },
    },
    // DataTable column headings
    MuiTableSortLabel: {
      styleOverrides: {
        root: ({ theme }): CSSInterpolation => ({
          fontWeight: 'bold',
          color: theme.palette.SlabGray['950'],
        }),
      },
    },
    // tab styles
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.SlabGray['600'],
          '&.Mui-selected': {
            color: theme.palette.SlabGray['800'],
          },
        }),
      },
    },
    // Forecast totals - row positioning
    MuiTableFooter: {
      styleOverrides: {
        root: ({ theme }): CSSInterpolation => ({
          position: 'sticky',
          bottom: 0,
          zIndex: theme.zIndex.tableHead,
          fontSize: 'inherit',
          backgroundColor: theme.palette.SlabGray[0],
        }),
      },
    },
    // Forecast totals - cell styles
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }): CSSInterpolation => ({
          color: theme.palette.SlabGray['800'],
        }),
        footer: {
          border: 0,
          borderTopWidth: 1,
          borderTopStyle: 'solid',
          borderTopColor: '#E0E0E0',
          fontSize: 'inherit',
          fontWeight: 'bold',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: ({ theme }): CSSInterpolation => ({
          borderColor: theme.palette.SlabGray.Stroke,
          marginTop: '0.5rem',
          marginBottom: '1rem',
          borderWidth: 1,
          borderStyle: 'solid',
          opacity: 1,
        }),
      },
    },
    // Top bar with Slabstack logo and tenant switcher menu
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }): CSSInterpolation => ({
          backgroundColor: theme.palette.SlabGray['50'],
          color: theme.palette.SlabGray['900'],
        }),
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: ({ theme }): CSSInterpolation => ({
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: theme.palette.SlabGray[500],
          backgroundColor: theme.palette.SlabGray[50],
          '& svg': {
            color: theme.palette.SlabGray[600],
          },
        }),
      },
    },
    MuiButton: {
      defaultProps: {
        size: 'large',
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          height: 'min-content',
          minWidth: 'unset',
          display: 'flex',
          gap: 8,
          '& .MuiButton-icon': {
            margin: 0,
          },
        },
      },
      variants: [
        {
          props: { children: undefined },
          style: { minWidth: 'unset', padding: 10 },
        },
        {
          props: { variant: 'slab-primary' },
          style: ({ theme }): CSSInterpolation => ({
            display: 'flex',
            fontWeight: 400,
            fontSize: '1.125rem',
            lineHeight: '1.125rem',
            textTransform: 'uppercase',
            height: 'min-content',
            borderRadius: 120,
            minWidth: 'unset',
            gap: 8,
            color: theme.palette.SlabGray[800],
            border: `1px solid ${theme.palette.SlabYellow[500]}`,
            backgroundColor: theme.palette.SlabYellow[500],
            padding: '16px 34px',
            '&:hover': {
              backgroundColor: theme.palette.SlabYellow[500],
              boxShadow: `0px 4px 24px 0px ${theme.palette.SlabYellow[500]}`,
            },
            '&:active': {
              backgroundColor: theme.palette.SlabYellow[500],
              boxShadow: `0px 4px 24px 0px ${theme.palette.SlabYellow[500]}`,
            },
            '&.MuiButton-sizeSmall': {
              gap: 8,
              fontSize: '0.875rem',
              padding: '8px 16px',
            },
            '&.MuiButton-sizeMedium': {
              gap: 8,
              fontSize: '0.875rem',
              padding: '12px 24px',
            },
            '&.Mui-disabled': {
              color: 'inherit',
              opacity: 0.65,
            },
          }),
        },
        {
          props: { variant: 'slab-primary', children: undefined },
          style: {
            display: 'flex',
            fontWeight: 400,
            fontSize: '1.125rem',
            lineHeight: '1.125rem',
            textTransform: 'uppercase',
            height: 'min-content',
            borderRadius: 120,
            minWidth: 'unset',
            gap: 8,
            padding: 14,
            '&.MuiButton-sizeSmall': {
              padding: 8,
            },
          },
        },
        {
          props: { variant: 'slab-secondary' },
          style: ({ theme }): CSSInterpolation => ({
            display: 'flex',
            fontWeight: 400,
            fontSize: '1.125rem',
            lineHeight: '1.125rem',
            textTransform: 'uppercase',
            height: 'min-content',
            borderRadius: 120,
            minWidth: 'unset',
            gap: 8,
            color: theme.palette.SlabGray[800],
            backgroundColor: 'transparent',
            border: `1px solid ${theme.palette.SlabGray[900]}`,
            padding: '16px 34px',
            '&:hover': {
              backgroundColor: 'transparent',
              boxShadow: `0px 4px 32px 0px ${theme.palette.SlabGray[400]}`,
            },
            '&:active': {
              backgroundColor: 'transparent',
              boxShadow: `0px 4px 32px 0px ${theme.palette.SlabGray[400]}`,
            },
            '&.MuiButton-sizeSmall': {
              gap: 8,
              fontSize: '0.875rem',
              padding: '8px 16px',
            },
            '&.MuiButton-sizeMedium': {
              gap: 8,
              fontSize: '0.875rem',
              padding: '12px 24px',
            },
            '&.Mui-disabled': {
              color: 'inherit',
              opacity: 0.35,
            },
          }),
        },
        {
          props: { variant: 'slab-secondary', children: undefined },
          style: {
            display: 'flex',
            fontWeight: 400,
            fontSize: '1.125rem',
            lineHeight: '1.125rem',
            textTransform: 'uppercase',
            height: 'min-content',
            borderRadius: 120,
            minWidth: 'unset',
            gap: 8,
            padding: 14,
            '&.MuiButton-sizeSmall': {
              padding: 8,
            },
          },
        },
        {
          props: { variant: 'slab-outline' },
          style: ({ theme }): CSSInterpolation => ({
            display: 'flex',
            fontWeight: 500,
            fontSize: 14,
            lineHeight: '1.125rem',
            textTransform: 'uppercase',
            height: 'min-content',
            borderRadius: 12,
            minWidth: 'unset',
            gap: 8,
            border: `1px solid ${theme.palette.SlabGray.Stroke}`,
            color: theme.palette.SlabIndigo[700],
            padding: 10,
            '&:hover': {
              backgroundColor: 'transparent',
            },
            '&:active': {
              backgroundColor: 'transparent',
            },
          }),
        },
        {
          props: { variant: 'slab-filled' },
          style: ({ theme }): CSSInterpolation => ({
            borderRadius: 8,
            maxHeight: 'fit-content',
            backgroundColor: theme.palette.SlabGray.Stroke,
            padding: 8,
            fontSize: 14,
            fontWeight: 400,
            textTransform: 'none',
            color: theme.palette.SlabIndigo[700],
            gap: 8,
            '&:hover': {
              backgroundColor: theme.palette.SlabGray.Stroke,
            },
            '&:active': {
              backgroundColor: theme.palette.SlabGray.Stroke,
            },
            '& svg': {
              width: 18,
              height: 18,
            },
          }),
        },
        {
          props: { variant: 'slab-action-bordered' },
          style: ({ theme }): CSSInterpolation => ({
            fontSize: 18,
            fontWeight: 400,
            lineHeight: 1.2,
            textTransform: 'none',
            borderRadius: 8,
            padding: '10px 16px',
            gap: 8,
            color: theme.palette.SlabIndigo[700],
            backgroundColor: theme.palette.SlabGray[0],
            border: `1px solid ${theme.palette.SlabGray.Border}`,
            '&:hover': {
              backgroundColor: theme.palette.SlabGray[50],
            },
            '&:active': {
              backgroundColor: theme.palette.SlabGray[50],
            },
            [theme.breakpoints.down('sm')]: {
              fontSize: 14,
              padding: '6px 12px',
            },
          }),
        },
        {
          props: { variant: 'slab-action-plain' },
          style: ({ theme }): CSSInterpolation => ({
            fontSize: 18,
            fontWeight: 400,
            lineHeight: 1.2,
            textTransform: 'none',
            borderRadius: 8,
            padding: '10px 16px',
            gap: 8,
            color: theme.palette.SlabIndigo[700],
            backgroundColor: 'transparent',
            border: '1px solid transparent',
            '&:hover': {
              backgroundColor: `${theme.palette.SlabIndigo[700]}0D`,
            },
            '&:active': {
              backgroundColor: `${theme.palette.SlabIndigo[700]}0D`,
            },
            [theme.breakpoints.down('sm')]: {
              fontSize: 14,
              padding: '6px 12px',
            },
          }),
        },
        {
          props: { variant: 'slab-transparent' },
          style: ({ theme }): CSSInterpolation => ({
            borderRadius: 4,
            textTransform: 'none',
            maxHeight: 'fit-content',
            backgroundColor: 'transparent',
            gap: 4,
            padding: 4,
            fontSize: 12,
            color: theme.palette.SlabIndigo[700],
            '&:hover': {
              backgroundColor: `${theme.palette.SlabIndigo[700]}0D`,
            },
            '&:active': {
              backgroundColor: `${theme.palette.SlabIndigo[700]}0D`,
            },
            '& svg': {
              width: 18,
              height: 18,
            },
            '&.MuiButton-sizeMedium': {
              gap: 8,
              padding: 6,
              fontSize: 14,
            },
            '&.MuiButton-sizeLarge': {
              gap: 12,
              padding: 12,
              fontSize: 18,
              '& svg': {
                width: 24,
                height: 24,
              },
            },
          }),
        },
      ],
    },
    MuiTabs: {
      styleOverrides: {
        root: ({ theme }): CSSInterpolation => ({
          p: 0,
          boxShadow: `inset 0px -2px 0px 0px ${theme.palette.SlabGray.Stroke}`,
        }),
        indicator: ({ theme }) => ({
          backgroundColor: theme.palette.SlabGray[800],
        }),
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
        placement: 'top',
        enterDelay: 200,
      },
      styleOverrides: {
        tooltip: ({ theme }): CSSInterpolation => ({
          backgroundColor: alpha(theme.palette.SlabGray[600], 0.9),
          color: theme.palette.SlabGray[0],
          fontSize: '0.875rem', // 14px
          fontWeight: 400,
          lineHeight: 'normal',
          padding: '4px 8px',
          borderRadius: 6,
        }),
        arrow: ({ theme }): CSSInterpolation => ({
          color: alpha(theme.palette.SlabGray[600], 0.9),
        }),
      },
    },
  },
});
